/**
 * Service class to communicate with our WebSockets server.
 */
export class EchoService {

    /**
     * The name of the pseudo-channel to register listeners for events that are sent to no specific channel.
     * These should only be events regarding the connection itself, e.g., connection details or pongs.
     *
     * @type {string}
     */
    static DEFAULT_CHANNEL = '_connection';

    /**
     * @param url The URL to the WebSocket server to connect to, including the protocol (ws or wss).
     * @param debug Whether to log debug messages in the console.
     */
    constructor(url, debug = false) {
        this.url = url;
        this.debug = debug;

        this.socket = null; // The WebSocket instance
        this.connectedAt = null; // The ms timestamp when the connection to the WebSocket server was established
        this.status = ConnectionStatus.Closed; // The status of the WebSocket connection
        this.connectionId = null; // The connection id of the client on the WebSocket server
        this.idleTimeoutDuration = 10 * 60; // The duration in seconds after which the connection gets automatically closed when no messages are sent on the connection
        this.connectionDuration = 2 * 60 * 60; // The duration in seconds a connection will remain active until automatically closed by the WebSocket server
        this.pingTimeout = null; // The timeout callback of the currently running ping request to prevent the WebSocket connection from becoming idle
        this.channels = {}; // The channels the client is currently subscribed to
        this.queuedMessages = []; // The messages that should have been sent but couldn't yet due to a missing opened connection -> will be sent as soon as the connection is established
    }

    /**
     * Creates a WebSocket connection to the url of the instance.
     */
    async #connect() {
        return new Promise((resolve, reject) => {

            // We do not want to create multiple connections
            if (this.status === ConnectionStatus.Connected || this.status === ConnectionStatus.Connecting) {
                resolve();
                return;
            }

            // When the connection is currently being in the closing process, we set the status to connecting to auto-reconnect after the closing is finished..
            // This might happen when the last connected channel is switched to another similar channel.
            if (this.status === ConnectionStatus.Closing) {
                this.status = ConnectionStatus.Connecting;
                reject("Existing connection is currently being closed. Will auto-connect after closing finished.");
                return;
            }

            this.status = ConnectionStatus.Connecting;
            this.socket = new WebSocket(this.url)

            // Register the handler that gets called as soon as the connection was successfully opened
            this.socket.onopen = () => {

                this.connectedAt = Date.now();

                // Register the listeners on the default connection channel
                this.subscribe().listen('pong', () => {

                    // Schedule a ping only after a successful pong for the previous ping was received,
                    // to prevent sending pings when no connection is open or an error occurred on the connection
                    this.#scheduleKeepAlivePing(this.idleTimeoutDuration / 2)
                }).listen('details', data => {
                    this.connectionId = data.connection_id;
                    this.idleTimeoutDuration = data.idle_timeout ?? (10 * 60);
                    this.connectionDuration = data.connection_duration ?? (2 * 60 * 60);

                    // We only resolve the successful connection when we received the connection id to ensure it's set for successful connections
                    this.status = ConnectionStatus.Connected;
                    resolve();

                    if (this.debug) {
                        console.log("WebSocket connection opened.")
                    }

                    // Send the messages that should have been sent but couldn't yet due to a missing opened connection
                    if (this.queuedMessages.length) {
                        this.queuedMessages.forEach((message, index) => {
                            delete this.queuedMessages[index];
                            this.#send(message.action, message.data);
                        })
                    }

                    // Schedule the initial ping
                    this.#scheduleKeepAlivePing(this.idleTimeoutDuration / 2)
                })

                // Request the initial connection details of the opened connection to set the connected state on our local service instance
                this.socket.send(JSON.stringify({action: 'details'}))
            };

            // Register the handler that gets called as soon as the connection was successfully closed
            this.socket.onclose = () => {
                if (this.pingTimeout) {
                    clearTimeout(this.pingTimeout)
                }

                // -5 for a short allowed init variety
                const closedDueToDurationElapse = this.status !== ConnectionStatus.Closing && this.connectedAt && this.connectionDuration && (Date.now() - this.connectedAt) >= ((this.connectionDuration - 5) * 1000);

                const shallReconnect = this.status === ConnectionStatus.Connecting || closedDueToDurationElapse;

                this.status = ConnectionStatus.Closed;
                this.connectionId = false;
                this.connectedAt = null;

                if (shallReconnect) {
                    if (this.debug) {
                        if (closedDueToDurationElapse) {
                            console.log("WebSocket connection duration elapsed, reconnecting...")
                        } else {
                            console.log("WebSocket connection closed, but reconnecting due to request...")
                        }
                    }

                    // We connect manually before subscribing, even though a subscription would auto-connect as well, to prevent potential race conditions with multiple connections
                    this.#connect().then(() => {
                        Object.keys(this.channels).forEach(channelName => {
                            this.subscribe(channelName);
                        })
                    });
                } else if (this.debug) {
                    console.log("WebSocket connection closed.")
                }
            };

            // Register the handler that gets called as soon as a message was received on the opened connection
            this.socket.onmessage = (event) => {
                const payload = JSON.parse(event.data);

                // Trigger the listeners on the specific channel the event is intended for
                const channelName = payload.channel ?? EchoService.DEFAULT_CHANNEL;
                const channel = this.channels[channelName];
                if (channel) {

                    const eventName = payload.event;
                    const eventData = payload.data;

                    // Remove all the data we do not want to pass as metadata
                    delete payload.channel;
                    delete payload.event;
                    delete payload.data;

                    channel.notify(eventName, eventData, {
                        ...payload,
                        same_connection: !!(payload.origin_connection_id && payload.origin_connection_id === this.connectionId)
                    });
                } else {
                    console.warn(`WebSocket message for unsubscribed channel '${channelName}' received:`, payload);
                }
            };

            // Register the handler that gets called as soon as an error occurred on the connection
            this.socket.onerror = (error) => {
                this.status = this.status === ConnectionStatus.Connecting ? ConnectionStatus.Closed : this.status;
                console.error('Error from echo:', error);
                reject(error);
            };
        });
    }

    /**
     * Closes the WebSocket connection to the instance's url.
     */
    #disconnect() {
        if (this.socket) {
            this.status = ConnectionStatus.Closing;
            this.channels = {};
            this.socket.close();
            this.socket = null;
        }
    }

    /**
     * Schedule a new ping request to be sent to the WebSocket server after the specified seconds,
     * to prevent the WebSocket connection to become idle and being closed by the server.
     *
     * @param timeoutInSeconds
     */
    #scheduleKeepAlivePing(timeoutInSeconds) {
        if (this.pingTimeout) {
            clearTimeout(this.pingTimeout)
        }
        this.pingTimeout = setTimeout(() => {
            this.#send('ping', {}, false);
        }, 1000 * timeoutInSeconds)
    }

    /**
     * Subscribes the client to the channel with the specified name.
     * This enables the client to register listeners on the channel that gets automatically called when a new message is received on the channel from the WebSocket server.
     * If no connection to the WebSocket exists yet, a connection will be automatically established in first place.
     *
     * @param channel The name of the channel to connect to.
     * @returns EchoChannel
     */
    subscribe(channel = EchoService.DEFAULT_CHANNEL) {
        const alreadySubscribed = !!this.channels[channel];

        if (!alreadySubscribed) {
            this.channels[channel] = new EchoChannel(channel, this.debug);
        }

        this.#connect().then(() => {
            // We do not need to subscribe to our pseudo default channel as the events for that channel are sent without any channel info
            if (channel !== EchoService.DEFAULT_CHANNEL) {

                // We only send a subscribe action when not already subscribed for the current connection id.
                // The current connection id might differ from the channel's connection id when resubscribing on reconnections after the connection duration elapsed.
                if (!alreadySubscribed || this.channels[channel].connectionId !== this.connectionId) {
                    this.#send('subscribe', {
                        channel: channel
                    })
                }
            }
            this.channels[channel].setConnectionId(this.connectionId);

            if (this.debug) {
                console.log(`Subscribed to channel [${channel}].`);
            }
        }).catch(error => {
            if (this.debug) {
                console.warn(`Could not connect for subscription to channel [${channel}].`, error);
            }
        });

        return this.channels[channel];
    }

    /**
     * Removes the subscription of the client to the channel with the specified name.
     * Additionally, if not other channel subscription exists anymore, the connection to the WebSocket server will be automatically closed.
     *
     * @param channel The name of the channel to disconnect to.
     */
    unsubscribe(channel) {
        if (this.channels[channel]) {
            this.#send('unsubscribe', {
                channel: channel
            });
            delete this.channels[channel];

            if (this.debug) {
                console.log(`Unsubscribed from channel [${channel}].`);
            }
        }

        // Ignore the default connection channel
        if (Object.keys(this.channels).length <= 1) {
            this.#disconnect();
        }
    }

    /**
     * Sends the specified event with the specified data to all connected clients on the specified channel.
     *
     * @param channel
     * @param event
     * @param data
     */
    broadcast(channel, event, data = {}) {
        this.#connect().then(() => {
            this.#send('event', {
                channel: channel,
                event: event,
                data: data
            })
        }).catch(error => {
            if (this.debug) {
                console.warn(`Could not connect for broadcasting on channel [${channel}].`, error);
            }
        });
    }

    /**
     * Sends the specified action with the specified data to the WebSocket server.
     *
     * @param action
     * @param data
     * @param queueWhenNotConnected
     */
    #send(action, data = {}, queueWhenNotConnected = true) {
        if (this.status === ConnectionStatus.Connected) {
            this.socket.send(JSON.stringify({
                action: action,
                ...data
            }))
        } else if (queueWhenNotConnected) {
            this.queuedMessages.push({
                action: action,
                data: data
            });
        }

    }

    /**
     * Gets the connection id the client received from the WebSocket server when connecting to the server.
     *
     * @returns {string|null}
     */
    getConnectionId() {
        return this.connectionId;
    }
}

class EchoChannel {

    constructor(name, debug = false) {
        this.name = name;
        this.debug = debug;

        this.listeners = {}; // The registered listeners for each event name to be called when a new event is received on the channel
    }

    /**
     * Registers a callback that gets automatically called when an event with the specified name gets received from the WebSocket server on the channel.
     *
     * @param eventName
     * @param callback
     * @param listenToSameConnection Whether the specified callback shall be triggered when the event origins from the same WebSocket connection as the current client's one.
     */
    listen(eventName, callback, listenToSameConnection = true) {
        if (this.debug) {
            console.log(`Listening for [${eventName}] on [${this.name}].`);
        }

        const listenerData = {
            callback: callback,
            same_connection: listenToSameConnection
        }

        if (this.listeners[eventName]) {
            this.listeners[eventName].push(listenerData);
        } else {
            this.listeners[eventName] = [listenerData]
        }

        return this;
    }

    /**
     * Notifies the registered listeners of the channel that a new message with the specified event name and data was received.
     *
     * @param eventName
     * @param data The data that belongs directly to the event.
     * @param meta Any data that does not belong to the event data itself but describes the event circumstances, e.g., the initializer, timestamp, etc.
     */
    notify(eventName, data, meta = {}) {
        if (this.debug) {
            console.log(`Received event [${eventName}] on [${this.name}]:`, {
                data: data,
                meta: meta
            });
        }

        if (this.listeners[eventName]) {
            this.listeners[eventName].forEach(listenerData => {

                // Only trigger the callback when not on the same connection or the listener wants to receive events on the same connection
                if (!meta.same_connection || listenerData.same_connection) {
                    listenerData.callback(data, meta);
                }
            })
        }
    }

    /**
     * Sets the connection id on which the subscription was activated.
     */
    setConnectionId(connectionId) {
        this.connectionId = connectionId;
    }
}

const ConnectionStatus = {
    Connecting: 'connecting',
    Connected: 'connected',
    Closing: 'closing',
    Closed: 'closed',
}